import React, { useState, useEffect } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import Image from "../images/Image";
import InputLabel from "../inputfields/InputLabel";
import InputShimmer from "../inputfields/InputShimmer";

dayjs.extend(customParseFormat);

interface CustomDatePickerProps {
  testID: string;
  label: string;
  handleStates: (date: dayjs.Dayjs | null, type: string) => void;
  value: dayjs.Dayjs | null;
  error?: string;
  disabled: boolean;
  type: "dob" | string;
  disabledDate?: boolean;
  fieldError?: boolean;
  divClassName?: string;
  id?: string;
  required: boolean;
  classType?: string;
  editLoading?: boolean;
  disabledPastDate?: boolean;
  note?: string;
  paddingTop?: string;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  testID,
  label,
  handleStates,
  divClassName,
  fieldError,
  value,
  error,
  disabled,
  id,
  type,
  disabledDate,
  disabledPastDate,
  required,
  classType,
  editLoading,
  note,
  paddingTop
}) => {
  const [popupPlacement, setPopupPlacement] = useState<'bottomLeft' | 'topLeft'>('bottomLeft');
  const [open, setOpen] = useState(false);

  const getDisabledDate = (current: dayjs.Dayjs) => {
    if (disabledDate) {
      return current && current > dayjs();
    }
    if (disabledPastDate) {
      return current && current < dayjs().startOf('day');
    }
    return false;
  };

  const adjustPopupPlacement = (triggerEl: HTMLElement) => {
    setTimeout(() => {
      const rect = triggerEl.getBoundingClientRect();
      const popupHeight = 300;
      const spaceBelow = window.innerHeight - rect.bottom;
      
      const newPlacement = spaceBelow < popupHeight ? 'topLeft' : 'bottomLeft';
      setPopupPlacement(newPlacement);
      
      // Force reposition the dropdown
      const dropdown = document.querySelector('.ant-picker-dropdown');
      if (dropdown && newPlacement === 'topLeft') {
        (dropdown as HTMLElement).style.top = `${rect.top - popupHeight}px`;
      }
    }, 0);
  };

  // Close date picker on scroll
  useEffect(() => {
    if (!open) return;

    const handleScroll = () => {
      // Close the date picker when any scroll event occurs
      setOpen(false);
    };

    // Use capture phase to catch all scroll events from any element
    // This will catch scroll events from window, document, and all nested scrollable containers
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  return (
    <div className={divClassName}>
      <InputLabel labelName={label} id={id || 'date_label'} required={required} />
      {editLoading ? (
        <InputShimmer className={divClassName} />
      ) : (
        <DatePicker
          disabled={disabled}
          open={open}
          className={` ${
            disabled
              ? `custom-datepicker-disabled${classType ? `-${classType}` : ""}`
              : error || fieldError
              ? `custom-datepicker-error${classType ? `-${classType}` : ""}`
              : `custom-datepicker${classType ? `-${classType}` : ""}`
          } w-full py-4 pl-4 pr-4 !shadow-none focus:!shadow-none`}
          autoComplete="off"
          data-testid={testID || id}
          placeholder={'Select date'}
          format={"DD MMM YYYY"}
          value={value}
          suffixIcon={<Image src="calendar" />}
          disabledDate={getDisabledDate}
          onChange={(date) => {
            handleStates(date, type);
          }}
          placement={popupPlacement}
          getPopupContainer={() => document.body}
          popupStyle={{ zIndex: 1050, position: 'fixed' }}
          onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (isOpen) {
              const target = document.querySelector<HTMLElement>(
                `[data-testid="${testID || id}"]`
              );
              if (target) adjustPopupPlacement(target);
            }
          }}
        />
      )}
      {error && <ErrorMessage testId={`${testID || id || 'date-picker'}-error`} paddingTop={paddingTop} errorMessage={error || ''} />}
      {note && <p className="text-sm font-medium text-gray-400 mt-1">{note}</p>}
    </div>
  );
};


export default CustomDatePicker;

// Export for test coverage
export const _test_getDisabledDate = (disabledDate?: boolean, disabledPastDate?: boolean) => (current: dayjs.Dayjs) => {
  if (disabledDate) {
    return current && current > dayjs();
  }
  if (disabledPastDate) {
    return current && current < dayjs().startOf('day');
  }
  return false;
};